import React, {useMemo, useRef, useEffect} from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from "chart.js";
import {Line} from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import {useChartTheme} from "../../../contexts/ChartThemeContext";
import {createTickFormatter} from "../../../utils/chartTicks";
import colors from "../../../styles/colors";
import {maxCrowdedness} from "../../../constants";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  annotationPlugin
);

export interface PrimaryMetricsChartProps {
  // FPS data
  fpsData?: {
    points: Array<[number, number]>; // [timestamp, fps]
    avg: number;
    pc1: number;
    max: number;
    min: number;
  };

  // UDP data
  udpData?: {
    points: Array<[number, number, number, number, number]>; // [timestamp, stc, rxerrors, cts, txerrors]
    avgStc: number;
    avgCts: number;
    maxStc: number;
    maxCts: number;
  };

  // Disconnect events
  tcpDisconnects?: {
    points: Array<[number, number]>; // [timestamp, value]
  };

  bleDisconnects?: {
    points: Array<[number, number]>; // [timestamp, value]
  };

  antDisconnects?: {
    points: Array<[number, number]>; // [timestamp, value]
  };

  // Common props
  axisSeries?: {
    name: string;
    data: Array<{timestamp: number; value: number}>;
    timeRange?: {start: number; end: number};
    ticks?: Array<{timestamp: number; label: string}>;
  };

  // Zoom synchronization props
  zoomState?: {min?: number; max?: number};
  onZoomChange?: (zoomState: {min?: number; max?: number}) => void;
  onResetZoom?: () => void;

  // Timezone offset (data is pre-localized, so this is for reference only)
  timezoneOffsetMinutes?: number;
}

const PrimaryMetricsChart: React.FC<PrimaryMetricsChartProps> = (props) => {
  const chartRef = useRef<ChartJS<"line", {x: number; y: number}[], unknown>>(null);
  const isUpdatingZoom = useRef(false);
  const {getThemeColors} = useChartTheme();
  const themeColors = getThemeColors();

  // Create tick formatter with consistent formatting
  // Note: Data is pre-localized in parent component, so we pass 0 for timezone offset
  const tickFormatter = useMemo(() => {
    return createTickFormatter(0);
  }, []);

  // Process FPS data for chart
  const fpsChartData = useMemo(() => {
    if (!props.fpsData?.points?.length) {
      return [];
    }

    return props.fpsData.points.map(([timestamp, fps]) => ({
      x: timestamp,
      y: fps
    }));
  }, [props.fpsData]);

  // Process UDP data for chart - StC only (Server to Client)
  const udpStcChartData = useMemo(() => {
    if (!props.udpData?.points?.length) {
      return [];
    }

    return props.udpData.points.map(([timestamp, stc]) => ({
      x: timestamp,
      y: stc // Server to Client traffic (varies with riders nearby)
    }));
  }, [props.udpData]);

  // Process disconnect events
  const disconnectEvents = useMemo(() => {
    const events: Array<{x: number; y: number; type: string; color: string}> = [];

    // TCP Disconnects - highest priority
    if (props.tcpDisconnects?.points) {
      props.tcpDisconnects.points.forEach(([timestamp]) => {
        events.push({
          x: timestamp,
          y: 50, // Fixed Y position for visibility
          type: "TCP Disconnect",
          color: colors.fiestared
        });
      });
    }

    // BLE Disconnects
    if (props.bleDisconnects?.points) {
      props.bleDisconnects.points.forEach(([timestamp]) => {
        events.push({
          x: timestamp,
          y: 40, // Fixed Y position
          type: "BLE Disconnect",
          color: colors.tacxblue
        });
      });
    }

    // ANT+ Disconnects
    if (props.antDisconnects?.points) {
      props.antDisconnects.points.forEach(([timestamp]) => {
        events.push({
          x: timestamp,
          y: 30, // Fixed Y position
          type: "ANT+ Disconnect",
          color: colors.orange
        });
      });
    }

    return events;
  }, [props.tcpDisconnects, props.bleDisconnects, props.antDisconnects]);

  // Prepare Chart.js data
  const chartData: ChartData<"line", {x: number; y: number}[]> = useMemo(() => {
    const datasets: any[] = [];

    // FPS Line (Left Y-axis)
    if (fpsChartData.length > 0) {
      datasets.push({
        label: "Graphics FPS",
        data: fpsChartData,
        borderColor: colors.brand,
        backgroundColor: colors.brand + "40",
        fill: true,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.4,
        yAxisID: "y" // Left axis
      });
    }

    // UDP StC Line (Right Y-axis) - Server to Client in yellow
    if (udpStcChartData.length > 0) {
      datasets.push({
        label: "UDP StC (Server to Client)",
        data: udpStcChartData,
        borderColor: colors.yellow,
        backgroundColor: colors.yellow + "40",
        fill: false,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
        yAxisID: "y1" // Right axis
      });
    }

    // Disconnect Events (Scatter points on left axis)
    const tcpEvents = disconnectEvents.filter((e) => e.type === "TCP Disconnect");
    const bleEvents = disconnectEvents.filter((e) => e.type === "BLE Disconnect");
    const antEvents = disconnectEvents.filter((e) => e.type === "ANT+ Disconnect");

    if (tcpEvents.length > 0) {
      datasets.push({
        label: "TCP Disconnects",
        data: tcpEvents,
        backgroundColor: colors.fiestared,
        borderColor: colors.fiestared,
        pointRadius: 6,
        pointHoverRadius: 8,
        showLine: false,
        yAxisID: "y"
      });
    }

    if (bleEvents.length > 0) {
      datasets.push({
        label: "BLE Disconnects",
        data: bleEvents,
        backgroundColor: colors.tacxblue,
        borderColor: colors.tacxblue,
        pointRadius: 6,
        pointHoverRadius: 8,
        showLine: false,
        yAxisID: "y"
      });
    }

    if (antEvents.length > 0) {
      datasets.push({
        label: "ANT+ Disconnects",
        data: antEvents,
        backgroundColor: colors.orange,
        borderColor: colors.orange,
        pointRadius: 6,
        pointHoverRadius: 8,
        showLine: false,
        yAxisID: "y"
      });
    }

    return {datasets};
  }, [fpsChartData, udpStcChartData, disconnectEvents]);

  // Apply zoom state from other charts
  useEffect(() => {
    if (chartRef.current && props.zoomState && !isUpdatingZoom.current) {
      const chart = chartRef.current;

      if (props.zoomState.min !== undefined && props.zoomState.max !== undefined) {
        isUpdatingZoom.current = true;

        try {
          (chart as any).zoomScale(
            "x",
            {
              min: props.zoomState.min,
              max: props.zoomState.max
            },
            "none"
          );
        } catch {
          try {
            if (chart.scales && chart.scales.x) {
              chart.scales.x.options.min = props.zoomState.min;
              chart.scales.x.options.max = props.zoomState.max;
              chart.update("none");
            }
          } catch {
            // Silently fail
          }
        }

        setTimeout(() => {
          isUpdatingZoom.current = false;
        }, 100);
      } else if (Object.keys(props.zoomState).length === 0) {
        isUpdatingZoom.current = true;

        try {
          (chart as any).resetZoom("none");
        } catch {
          try {
            if (chart.scales && chart.scales.x) {
              chart.scales.x.options.min = props.axisSeries?.timeRange?.start;
              chart.scales.x.options.max = props.axisSeries?.timeRange?.end;
              chart.update("none");
            }
          } catch {
            // Silently fail
          }
        }

        setTimeout(() => {
          isUpdatingZoom.current = false;
        }, 100);
      }
    }
  }, [props.zoomState, props.axisSeries]);

  // Chart options
  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: {
        mode: "index" as const,
        intersect: false
      },
      onClick: (event, activeElements, chart) => {
        // Double click to reset zoom
        const now = Date.now();
        const lastClick = (chart as any).lastClickTime || 0;
        if (now - lastClick < 300) {
          chart.resetZoom();
        }
        (chart as any).lastClickTime = now;
      },
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            color: themeColors.text,
            usePointStyle: true,
            padding: 15
          }
        },
        tooltip: {
          enabled: false // Disabled due to alignment issues with dual y-axis
        },
        zoom: {
          limits: {
            x: {
              min: props.axisSeries?.timeRange?.start || 0,
              max: props.axisSeries?.timeRange?.end || Date.now()
            }
          },
          zoom: {
            wheel: {
              enabled: false
            },
            drag: {
              enabled: true,
              backgroundColor: "rgba(125,206,160,0.3)"
            },
            mode: "x" as const,
            onZoomComplete: ({chart}) => {
              if (props.onZoomChange && !isUpdatingZoom.current) {
                const xScale = chart.scales.x;
                props.onZoomChange({
                  min: xScale.min,
                  max: xScale.max
                });
              }
            }
          },
          pan: {
            enabled: true,
            mode: "x" as const,
            onPanComplete: ({chart}) => {
              if (props.onZoomChange && !isUpdatingZoom.current) {
                const xScale = chart.scales.x;
                props.onZoomChange({
                  min: xScale.min,
                  max: xScale.max
                });
              }
            }
          }
        },
        annotation: {
          annotations: {
            // FPS reference lines
            ...(props.fpsData && {
              fps60Line: {
                type: "line" as const,
                scaleID: "y",
                value: 60,
                borderColor: colors.green,
                borderWidth: 2,
                borderDash: [5, 5],
                label: {
                  display: true,
                  content: "60 FPS",
                  position: "end" as const,
                  backgroundColor: colors.green,
                  color: "white",
                  font: {size: 11, weight: "bold" as const},
                  padding: 3
                }
              },
              fpsAvgLine: {
                type: "line" as const,
                scaleID: "y",
                value: props.fpsData.avg,
                borderColor: colors.brand,
                borderWidth: 1,
                borderDash: [3, 3],
                label: {
                  display: true,
                  content: `Avg FPS (${Math.round(props.fpsData.avg)})`,
                  position: "start" as const,
                  backgroundColor: colors.brand + "CC",
                  color: "white",
                  font: {size: 10},
                  padding: 2
                }
              }
            }),

            // UDP reference lines
            ...(props.udpData && {
              udpAvgLine: {
                type: "line" as const,
                scaleID: "y1",
                value: props.udpData.avgStc,
                borderColor: colors.yellow,
                borderWidth: 1,
                borderDash: [3, 3],
                label: {
                  display: true,
                  content: `Avg StC (${Math.round(props.udpData.avgStc)})`,
                  position: "start" as const,
                  backgroundColor: colors.yellow + "CC",
                  color: "black",
                  font: {size: 10},
                  padding: 2
                }
              },

              // Crowding tier lines for UDP StC
              // Fondo threshold: 80% of max = 2800
              fondoLine: {
                type: "line" as const,
                scaleID: "y1",
                value: maxCrowdedness * 0.8,
                borderColor: colors.fiestared + "80",
                borderWidth: 1,
                borderDash: [3, 3],
                label: {
                  display: true,
                  content: "Fondo (80%)",
                  position: "end" as const,
                  backgroundColor: colors.fiestared + "CC",
                  color: "white",
                  font: {size: 10},
                  padding: 2
                }
              },
              // Packed threshold: 60% of max = 2100
              packedLine: {
                type: "line" as const,
                scaleID: "y1",
                value: maxCrowdedness * 0.6,
                borderColor: colors.orange + "80",
                borderWidth: 1,
                borderDash: [3, 3],
                label: {
                  display: true,
                  content: "Packed (60%)",
                  position: "end" as const,
                  backgroundColor: colors.orange + "CC",
                  color: "white",
                  font: {size: 10},
                  padding: 2
                }
              },
              // Crowded threshold: 40% of max = 1400
              crowdedLine: {
                type: "line" as const,
                scaleID: "y1",
                value: maxCrowdedness * 0.4,
                borderColor: colors.yellow + "80",
                borderWidth: 1,
                borderDash: [3, 3],
                label: {
                  display: true,
                  content: "Crowded (40%)",
                  position: "end" as const,
                  backgroundColor: colors.yellow + "CC",
                  color: "black",
                  font: {size: 10},
                  padding: 2
                }
              },
              // Company threshold: 20% of max = 700
              companyLine: {
                type: "line" as const,
                scaleID: "y1",
                value: maxCrowdedness * 0.2,
                borderColor: colors.brand + "80",
                borderWidth: 1,
                borderDash: [3, 3],
                label: {
                  display: true,
                  content: "Company (20%)",
                  position: "end" as const,
                  backgroundColor: colors.brand + "CC",
                  color: "white",
                  font: {size: 10},
                  padding: 2
                }
              }
            })
          }
        }
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          title: {
            display: true,
            text: "Time",
            color: themeColors.text
          },
          grid: {
            display: true,
            color: themeColors.grid + "30"
          },
          ticks: {
            color: themeColors.text,
            font: {size: 11},
            maxTicksLimit: 20,
            autoSkip: true,
            callback: function (value) {
              return tickFormatter(value);
            }
          },
          min: props.axisSeries?.timeRange?.start,
          max: props.axisSeries?.timeRange?.end
        },
        y: {
          type: "linear",
          display: true,
          position: "left",
          title: {
            display: true,
            text: "Graphics FPS / Events",
            color: themeColors.text
          },
          grid: {
            display: true,
            color: themeColors.grid + "20"
          },
          ticks: {
            color: themeColors.text,
            font: {size: 11}
          },
          min: 0,
          max: props.fpsData ? Math.max(120, props.fpsData.max) : 120
        },
        y1: {
          type: "linear",
          display: true,
          position: "right",
          title: {
            display: true,
            text: "UDP StC Packets (Server to Client)",
            color: themeColors.text
          },
          grid: {
            drawOnChartArea: false // Only draw grid for left axis
          },
          ticks: {
            color: themeColors.text,
            font: {size: 11}
          },
          min: 0
        }
      }
    }),
    [themeColors, tickFormatter, props.fpsData, props.udpData, props.axisSeries, props.onZoomChange]
  );

  if (!fpsChartData.length && !udpStcChartData.length && disconnectEvents.length === 0) {
    return (
      <div style={{height: "400px", display: "flex", alignItems: "center", justifyContent: "center"}}>
        <p>No data available for primary metrics</p>
      </div>
    );
  }

  return (
    <div style={{height: "400px", width: "100%"}}>
      <Line ref={chartRef} data={chartData} options={options} />
    </div>
  );
};

export default PrimaryMetricsChart;
